import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { calculateMaterialGridTemplateColumns } from './MaterialList';
import FilterCardHeader from 'components/DesignSystems/FilterHeader/FilterCardHeader';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import FormTextField from 'components/IAP/formFields/FormTextField';
import { FormProvider, useForm } from 'react-hook-form';
import FormNumericField from 'components/IAP/formFields/FormNumericField';
import { Material } from 'components/IAP/hooks/useSafetyAnalysis';

export type MaterialEditFormValues = Material;

const MaterialEdit: React.FC<{
  singleMaterial: Material;
  setIsEditing: (editing: boolean) => void;
  updateMaterial: (singleMaterial: Material) => void;
}> = ({ singleMaterial: material, setIsEditing, updateMaterial }) => {
  const formMethods = useForm<MaterialEditFormValues>({
    mode: 'onChange',
    defaultValues: {
      id: material.id,
      materialName: material.materialName,
      quantity: material.quantity || 1,
      physState: material.physState,
      niosh: material.niosh,
      specificGravity: material.specificGravity,
      ph: material.ph,
      idlh: material.idlh,
      flashPoint: material.flashPoint,
      lel: material.lel,
      uel: material.uel,
    },
  });

  const { handleSubmit } = formMethods;

  const onSubmit = (data: MaterialEditFormValues) => {
    updateMaterial({
      ...data,
      quantity:
        data.quantity === null || data.quantity === undefined
          ? 1
          : Number(data.quantity),
      ph: data.ph === null || data.ph === undefined ? 0 : Number(data.ph),
    });
    setIsEditing(false);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FilterCardHeader
          gridTemplateColumns={calculateMaterialGridTemplateColumns(9)}
        >
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="materialName" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormNumericField
              title=""
              fieldName="quantity"
              min={1}
              rules={{
                validate: (value: any) => {
                  const numericValue = Number(value);
                  if (isNaN(numericValue)) {
                    return 'Please enter a valid number';
                  }
                  if (numericValue < 1) {
                    return `Value must be at least ${1}`;
                  }
                  return true;
                },
              }}
            />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="physState" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="niosh" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="specificGravity" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormNumericField
              title=""
              fieldName="ph"
              rules={{
                validate: (value: any) => {
                  const numericValue = Number(value);
                  if (isNaN(numericValue)) {
                    return 'Please enter a valid number';
                  }
                  if (numericValue < 0) {
                    return `Value must be at least ${0}`;
                  }
                  if (numericValue > 14) {
                    return `Value must be at most ${14}`;
                  }
                  return true;
                },
              }}
            />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="idlh" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="flashPoint" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="lel" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="" fieldName="uel" />
          </div>
          <div className="d-flex align-items-center gap-1">
            <StylishNewButton className={`button--icon`} type="submit">
              <SharedIcon iconName="check" />
            </StylishNewButton>
            <StylishNewButton
              className={`button--icon`}
              type="button"
              onClick={(e) => {
                setIsEditing(false);
                e.stopPropagation();
              }}
            >
              <SharedIcon iconName="cancel" />
            </StylishNewButton>
          </div>
        </FilterCardHeader>
      </form>
    </FormProvider>
  );
};

export default MaterialEdit;
